import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { TranslatePipe } from '@recruitee/i18n';
import {
  IconName,
  KeyCode,
  IconComponent,
  IconColorDirective,
  RtTooltip,
  ButtonComponent,
  GroupComponent,
} from '@recruitee/user-interface';
import { asViewRef } from '@recruitee/utils';

export const BUTTON_SELECT_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @angular-eslint/no-forward-ref
  useExisting: forwardRef(() => InterviewButtonSelectComponent),
  multi: true,
};

type Button = {
  name: string;
  icon: IconName;
  class: string;
};

@Component({
  selector: 'rt-interview-button-select',
  templateUrl: 'interview-button-select.component.html',
  styleUrls: ['./interview-button-select.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BUTTON_SELECT_VALUE_ACCESSOR],
  host: {
    '[class.disabled]': 'disabled',
  },
  standalone: true,
  imports: [
    GroupComponent,
    ButtonComponent,
    RtTooltip,
    IconColorDirective,
    IconComponent,
    TranslatePipe,
  ],
})
export class InterviewButtonSelectComponent implements ControlValueAccessor {
  @Input()
  public buttons: Button[] = [
    {
      name: 'no',
      icon: 'rating-thumb-down',
      class: 'error',
    },
    {
      name: 'neutral',
      icon: 'thumbs-up-down',
      class: 'warning',
    },
    {
      name: 'yes',
      icon: 'rating-thumb-up',
      class: 'success',
    },
    {
      name: 'strong_yes',
      icon: 'rating-double-thumb-up',
      class: 'success',
    },
  ];
  @Input() public horizontal: boolean;
  @Input() public disabled: boolean = false;
  @Input() public short: boolean = false;
  @Input() public quickEvaluationStyle: boolean = false;
  @Output() public rateButtonClick: EventEmitter<void> = new EventEmitter<void>();

  private _value: string | null;

  public get value(): string | null {
    return this._value;
  }

  public set value(value: string | null) {
    if (this._value !== value) {
      this.rateButtonClick.emit();
    }

    this._value = value;
    this.onChange(this._value);
    if (!asViewRef(this.cdRef).destroyed) {
      this.cdRef.detectChanges();
    }
  }

  constructor(private cdRef: ChangeDetectorRef) {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onChange = (value: string | null) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched = () => {};

  public onClick(value: string): void {
    this.value = this.value === value ? null : value;
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }
    const key = event.which || event.keyCode;
    switch (key) {
      case KeyCode.LeftArrow:
        event.preventDefault();
        this.selectLeft();
        break;

      case KeyCode.RightArrow:
        event.preventDefault();
        this.selectRight();
        break;

      default:
        break;
    }
  }

  public isChecked(name: string): boolean {
    return this._value === name;
  }

  public trackByButton(index: number, button: Button): string | number {
    return button.name || index;
  }

  public writeValue(value: string): void {
    this._value = value;
    if (!asViewRef(this.cdRef).destroyed) {
      this.cdRef.detectChanges();
    }
  }

  public registerOnChange(fn: () => {}): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  private selectLeft(): void {
    const checkedOption = this.buttons.findIndex(button => button.name === this.value);

    if (checkedOption === -1) {
      const [first] = this.buttons || [];

      this.value = first.name;
      return;
    }

    if (checkedOption === 0 || checkedOption > this.buttons.length - 1) {
      this.value = this.buttons[this.buttons.length - 1].name;
    } else {
      this.value = this.buttons[checkedOption - 1].name;
    }
  }

  private selectRight(): void {
    const checkedOption = this.buttons.findIndex(button => button.name === this.value);

    if (checkedOption === -1) {
      const [first] = this.buttons || [];
      this.value = first.name;
      return;
    }

    if (checkedOption >= this.buttons.length - 1) {
      this.value = this.buttons[0].name;
    } else {
      this.value = this.buttons[checkedOption + 1].name;
    }
  }
}
